import { TextTestID } from "@/constants/TestID";
import { FAIconName, Icon, Text } from "@/ui/core";
import { Box, HStack, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

type HelpCenterCardContentVariant = "default" | "mealSupport";
interface HelpCenterCardProps {
  contentVariant?: HelpCenterCardContentVariant;
  onPress: () => void;
}

interface HelpCenterCardContent {
  description: string;
  iconName: FAIconName;
  title: string;
}

const HelpCenterCardContentMap: Record<
  HelpCenterCardContentVariant,
  HelpCenterCardContent
> = {
  default: {
    description:
      "Help with scheduling, billing, insurance, tech support, and more.",
    iconName: FAIconName.HeadsetSolid,
    title: "Equip Service Desk",
  },
  mealSupport: {
    description:
      "Chat with a specialist now to get immediate help with nutrition and mealtimes.",
    iconName: FAIconName.MessageExclamation,
    title: "On Demand Meal Support",
  },
};

const HelpCenterCard = ({
  contentVariant = "default",
  onPress,
}: HelpCenterCardProps) => {
  const baseTestID = "help-center-card";

  const { description, iconName, title } =
    HelpCenterCardContentMap[contentVariant];

  return (
    <HStack
      alignItems="center"
      backgroundColor={ColorToken.Primary10}
      borderRadius={12}
      gap={8}
      justifyContent="space-between"
      onPress={onPress}
      padding={18}
      testID={joinTestIDs(baseTestID, TextTestID.Card)}
      width="100%"
    >
      <VStack flex={1} gap={8}>
        <HStack alignItems="center" gap={6}>
          <Icon
            color={ColorToken.Primary100}
            name={iconName}
            size="md"
            testID={joinTestIDs(baseTestID, iconName)}
          />
          <Text
            color={ColorToken.Primary100}
            testID={joinTestIDs(baseTestID, TextTestID.Title)}
            variant="bodyMedium"
          >
            {title}
          </Text>
        </HStack>
        <Text
          testID={joinTestIDs(baseTestID, TextTestID.Description)}
          variant="caption"
        >
          {description}
        </Text>
      </VStack>
      <Box alignItems="center" justifyContent="center">
        <Icon
          color={ColorToken.Primary100}
          name={FAIconName.ChevronRight}
          size="md"
          testID={joinTestIDs(baseTestID, FAIconName.ChevronRight)}
        />
      </Box>
    </HStack>
  );
};

export type {
  HelpCenterCardContent,
  HelpCenterCardContentVariant,
  HelpCenterCardProps,
};

export { HelpCenterCardContentMap };
export default HelpCenterCard;
